import { useEffect, useState } from 'react';
import { singletonHook } from 'react-singleton-hook';
import {
  NavigationInterceptionCapability,
  Specs,
} from '@doxyme/extensions-bridge';
import { useAddCapability } from '@doxyme/extensions-bridge-react';
import { bridge } from '../../../features/extensions/instances';
import { NavigationConfirmationAlertProps } from '../../../components/navigationConfirmationAlert';

export type NavigationConfirmationReason = {
  id: string;
  title: string;
  description: string;
  stayButtonText: string;
  leaveButtonText: string;
} & Pick<
  NavigationConfirmationAlertProps,
  'statusIcon' | 'allowClientSideNavigation'
>;

type NavigationAddPayload = Specs.NavigationInterception.NavigationAddPayload;
type NavigationRemovePayload =
  Specs.NavigationInterception.NavigationRemovePayload;

export type UseExtensionsNavigationInterceptionReturnType = {
  navigationConfirmationReasons: NavigationConfirmationReason[];
  addNavigationConfirmationReason: (
    reason: NavigationConfirmationReason,
  ) => void;
  removeNavigationConfirmationReason: (payload: { id: string }) => void;
  clearNavigationConfirmationReasons: () => void;
};

const hookInitValues: UseExtensionsNavigationInterceptionReturnType = {
  navigationConfirmationReasons: [],
  addNavigationConfirmationReason: () => void 0,
  removeNavigationConfirmationReason: () => void 0,
  clearNavigationConfirmationReasons: () => void 0,
};

export const useExtensionsNavigationInterceptionImpl =
  (): UseExtensionsNavigationInterceptionReturnType => {
    const [navigationConfirmationReasons, setNavigationConfirmationReasons] =
      useState<NavigationConfirmationReason[]>([]);

    const addNavigationConfirmationReason = (
      reason: NavigationConfirmationReason,
    ) => {
      setNavigationConfirmationReasons((prev) => {
        const idx = prev.findIndex((r) => r.id === reason.id);
        if (idx !== -1) {
          const updated = [...prev];
          updated[idx] = reason;
          return updated;
        }
        return [...prev, reason];
      });
    };

    const removeNavigationConfirmationReason = (payload: { id: string }) => {
      setNavigationConfirmationReasons((prev) =>
        prev.filter((r) => r.id !== payload.id),
      );
    };

    const clearNavigationConfirmationReasons = () => {
      setNavigationConfirmationReasons([]);
    };

    const navigationInterception = useAddCapability(
      bridge,
      () => new NavigationInterceptionCapability(),
    );

    useEffect(() => {
      const unsubscribeBlock = navigationInterception.onAdd(
        (payload: NavigationAddPayload): void => {
          addNavigationConfirmationReason(
            payload as NavigationConfirmationReason,
          );
        },
      );

      const unsubscribeAllow = navigationInterception.onRemove(
        (payload: NavigationRemovePayload): void => {
          removeNavigationConfirmationReason(payload);
        },
      );

      return () => {
        unsubscribeBlock();
        unsubscribeAllow();
      };
    }, []);

    return {
      navigationConfirmationReasons,
      addNavigationConfirmationReason,
      removeNavigationConfirmationReason,
      clearNavigationConfirmationReasons,
    };
  };

export const useExtensionsNavigationInterception = singletonHook(
  hookInitValues,
  useExtensionsNavigationInterceptionImpl,
);
